// The selected entity's samples for the detail panel's graphs.
import { computed, onUnmounted, ref, watch, type ComputedRef } from 'vue'
import { api, type Sample } from '../../api'
import { useNode, type Selection } from '../../store'

// liveRange is a live window of seconds ending now, in unix seconds, now
// being the node's clock (node.now): a laptop 40 s behind asked for a window
// that ended 40 s early, and the graphs lost their newest 40 s at every full
// reload (WEB-40)
export function liveRange(nowMs: number, seconds: number): [number, number] {
    const to = Math.floor(nowMs / 1000)
    return [to - seconds, to]
}

export function useSeries(sel: ComputedRef<Selection | null>) {
    const node = useNode()
    // series for sparklines: while live, the newest samples every second (as
    // the list's figures update) and the whole window every 30 s; windows beyond
    // an hour are minute averages, whose last two minutes the one-second samples
    // replace
    const series = ref<Sample[]>([])
    const loading = ref(true) // the graphs' first samples for this selection and window are coming
    let timer: number | undefined
    // graphs cover the selected time window (at least 5 minutes); the node
    // serves one-second samples up to an hour and one-minute averages beyond
    const graphSeconds = computed(() => Math.max(300, Math.round((node.span[1] - node.span[0]) / 1000)))
    const graphFrom = computed(() => node.span[1] - graphSeconds.value * 1000)
    // absolute windows are fetched by from/to so a past day shows that day
    const graphRange = computed(() =>
        node.live ? null : ([Math.floor(graphFrom.value / 1000), Math.floor(node.span[1] / 1000)] as const),
    )
    const graphLabel = computed(() =>
        graphSeconds.value <= 3600
            ? `last ${Math.round(graphSeconds.value / 60)} min`
            : node.live
              ? `last ${'preset' in node.window ? node.window.preset : ''}`
              : 'selected window, minute averages',
    )
    // a slow answer for an earlier selection or window (a 7-day machine series)
    // must not overwrite a newer one
    let seriesGen = 0
    async function loadSeries() {
        const g = seriesGen
        if (!sel.value) {
            series.value = []
            return
        }
        const [from, to] = graphRange.value ?? liveRange(node.now, graphSeconds.value)
        try {
            const s = await api.series(sel.value.kind, sel.value.id, from, to)
            if (g === seriesGen) series.value = s
        } catch {
            /* ignore */
        }
    }
    // the last two minutes at one-second resolution, replacing the minute
    // averages there (a minute appears in those only once it is complete)
    async function loadTail() {
        const g = seriesGen
        if (!sel.value) return
        const now = liveRange(node.now, 0)[1]
        try {
            const tail = await api.seriesRecent(sel.value.kind, sel.value.id, 120)
            if (!tail.length || g !== seriesGen) return
            const first = tail[0]!.t
            series.value = [...series.value.filter((x) => x.t < first && x.t >= now - graphSeconds.value), ...tail]
        } catch {
            /* ignore */
        }
    }
    watch(
        [sel, graphSeconds, graphRange],
        () => {
            clearInterval(timer)
            seriesGen++
            const g = seriesGen
            series.value = [] // another entity's or window's samples are not these
            loading.value = true
            const full = () =>
                loadSeries().then(() => {
                    if (g === seriesGen) loading.value = false
                    if (node.live) void loadTail()
                })
            void full()
            let tick = 0
            timer = window.setInterval(
                () => {
                    if (!node.connected) return // its 503s cost a request a second
                    if (!node.live)
                        void loadSeries() // a past window: nothing new, a slow refresh
                    else if (++tick % 30 === 0) void full()
                    else void loadTail()
                },
                node.live ? 1000 : 60000,
            )
        },
        { immediate: true },
    )
    onUnmounted(() => clearInterval(timer))
    // back from a hidden tab: its timers ran once a minute, the graph has gaps
    const onVisibility = () => {
        if (!document.hidden && node.live) void loadSeries().then(loadTail)
    }
    document.addEventListener('visibilitychange', onVisibility)
    onUnmounted(() => document.removeEventListener('visibilitychange', onVisibility))
    return { series, loading, times: computed(() => series.value.map((s) => s.t)), graphFrom, graphLabel }
}
